<template>
    <modal id="finance-category-modal"
           size="large"
           v-model="showModal"
           :title="$t('category')"
           @submit="submitData" :loading="loading"
           :preloader="preloader"
           :scrollable="false"
    >
        <form
            ref="form"
            :data-url='selectedUrl ? selectedUrl : `${TENANT_BASE_URL}app/finance-categories`'
            @submit.prevent="submitData"
        >
            <div class="row">
                <div class="col-md-12">
                    <app-form-group
                        :label="$t('language')"
                        type="select"
                        v-model="locate"
                        :list="languages"
                    />
                </div>
                <div class="col-md-12">
                    <app-form-group
                        type="text"
                        :label="$t('name')"
                        :placeholder="$placeholder('name')"
                        v-model="formData.name[locate]"
                        :required="true"
                        :error-message="$errorMessage(errors, 'name')">
                    </app-form-group>
                </div>
                <div class="col-md-12">
                    <div class="form-group icon-picker-wrapper" ref="iconPicker">
                        <label>{{ $t('icon') }}</label>

                        <div
                            class="icon-picker-select form-control d-flex align-items-center justify-content-between"
                            :class="{'is-invalid': $errorMessage(errors, 'icon')}"
                            @click="toggleIconDropdown"
                        >
                            <div class="d-flex align-items-center">
                                <span class="icon-picker-select__icon" :key="formData.icon || 'search'">
                                    <i :data-feather="formData.icon || 'search'"></i>
                                </span>
                                <span class="text-muted flex-grow-1" v-if="!formData.icon">
                                    {{ $t('search') }}
                                </span>
                                <span v-else class="font-weight-500">{{ selectedIconLabel || formData.icon }}</span>
                            </div>
                            <app-icon id="select-icon" :name="showIconDropdown ? 'chevron-up' : 'chevron-down'"/>
                        </div>

                        <transition name="fade">
                            <div v-if="showIconDropdown" class="icon-picker-dropdown card shadow-sm">
                                <div class="card-body p-2">
                                    <div class="input-group icon-picker__search mb-2">
                                        <input
                                            v-model="iconSearch"
                                            type="text"
                                            class="form-control"
                                            :placeholder="$t('search')"
                                        />
                                    </div>

                                    <div class="icon-picker__grid custom-scrollbar">
                                        <button
                                            v-for="icon in filteredIcons"
                                            :key="icon.id"
                                            type="button"
                                            class="icon-picker__item"
                                            :class="{selected: formData.icon === icon.id}"
                                            :title="icon.id"
                                            @click.stop="selectIcon(icon.id)"
                                        >
                                            <i :data-feather="icon.id"></i>
                                        </button>
                                        <div
                                            v-if="!filteredIcons.length"
                                            class="text-muted text-center py-3 w-100"
                                            style="grid-column: 1 / -1;"
                                        >
                                            {{ $t('no_options_found') }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>

                        <small class="text-danger" v-if="$errorMessage(errors, 'icon')">
                            {{ $errorMessage(errors, 'icon') }}
                        </small>
                    </div>
                </div>
                <div class="col-md-12">
                    <app-form-group-selectable
                        type="select"
                        :label="$t('type')"
                        list-value-field="name"
                        v-model="formData.type"
                        :chooseLabel="$t('type')"
                        :required="true"
                        :error-message="$errorMessage(errors, 'type')"
                        :options="categoryTypeOptions"
                    />
                </div>
                <div class="col-md-12">
                    <app-form-group
                        type="text"
                        :label="$t('subject')"
                        :placeholder="$placeholder('subject')"
                        v-model="formData.subject"
                        :error-message="$errorMessage(errors, 'subject')">
                    </app-form-group>
                </div>
                <div class="col-md-12">
                    <app-form-group
                        type="color"
                        :label="$t('color')"
                        :placeholder="$placeholder('color')"
                        v-model="formData.color"
                        :error-message="$errorMessage(errors, 'color')">
                    </app-form-group>
                </div>
                <div class="col-md-12">
                    <app-form-group
                        type="switch"
                        :label="$t('active')"
                        v-model="formData.active"
                        :error-message="$errorMessage(errors, 'active')">
                    </app-form-group>
                </div>
                <div class="col-md-12">
                    <app-form-group
                        type="textarea"
                        :label="$t('remark')"
                        :placeholder="$textAreaPlaceHolder('remark')"
                        v-model="formData.remark[locate]"
                        :text-area-rows="7"
                        :error-message="$errorMessage(errors, 'remark')">
                    </app-form-group>
                </div>
            </div>
        </form>
    </modal>
</template>

<script>
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import {TENANT_BASE_URL} from "../../../../common/Config/UrlHelper";
import {formDataAssigner} from "../../../../common/Helper/Support/FormHelper";
import feather from "feather-icons";
import { FEATHER_ICON_IDS } from "../../../../common/Helper/common";
import { mapState } from "vuex";

export default {
    name: "FinanceCategoryCreate",
    mixins: [FormHelperMixins, ModalMixin],
    data() {
        return {
            TENANT_BASE_URL,
            locate: window?.appLanguage?.toLowerCase() || 'ja',
            iconOptions: [],
            iconSearch: '',
            showIconDropdown: false,
            categoryTypeOptions: [
                { id: 'income', name: this.$t('income') },
                { id: 'expense', name: this.$t('expense') }
            ],
            formData: {
                id: null,
                name: {},
                type: '',
                icon: '',
                subject: '',
                color: '#46c35f',
                active: true,
                remark: {},
            }
        }
    },
    computed: {
        ...mapState({
            languages: state => state.support.languages
        }),
        filteredIcons() {
            if (!this.iconSearch) {
                return this.iconOptions;
            }

            const keyword = this.iconSearch.toLowerCase();
            return this.iconOptions.filter(icon => {
                const plainName = icon.name.replace(/<[^>]+>/g, '').toLowerCase();
                return icon.id.toLowerCase().includes(keyword) || plainName.includes(keyword);
            });
        },
        selectedIconLabel() {
            const match = this.iconOptions.find(icon => icon.id === this.formData.icon);
            if (!match) return '';
            return match.name.replace(/<[^>]+>/g, '');
        }
    },
    methods: {
        resetForm() {
            this.errors = {};
            this.formData = {
                id: null,
                name: {},
                type: '',
                icon: '',
                subject: '',
                color: '#46c35f',
                active: true,
                remark: {},
            };
            this.iconSearch = '';
            this.showIconDropdown = false;
            this.locate = window?.appLanguage?.toLowerCase() || 'ja';
        },
        getFinanceCategory() {
            this.$axios.get(this.selectedUrl)
                .then(response => {
                    this.formData = {
                        ...response.data,
                        name: this.normalizeLocaleObject(response.data.name),
                        remark: this.normalizeLocaleObject(response.data.remark),
                    };
                })
                .catch(error => {
                    this.$toastError(error.message);
                });
        },
        submitData() {
            let url      = this.$refs.form.dataset['url'];
            let formData = formDataAssigner(new FormData, this.formData);

            if (this.formData.name) {
                formData.set('name', JSON.stringify(this.formData.name));
            }
            if (this.formData.remark) {
                formData.set('remark', JSON.stringify(this.formData.remark));
            }
            
            // Convert boolean to integer for backend validation
            if (this.formData.active !== undefined) {
                formData.set('active', this.formData.active ? 1 : 0);
            }

            if (this.selectedUrl) {
                // for file update need to send by post.
                formData.append('_method', 'PATCH');
            }

            this.loading = true;
            this.submitFromFixin('post', url, formData);
        },
        afterSuccess({data}) {
            this.resetForm();
            this.iconSearch = '';
            this.showIconDropdown = false;
            $('#finance-category-modal').modal('hide');
            this.$emit('input', false);
            this.toastAndReload(data.message, 'finance-category-table');
        },
        afterSuccessFromGetEditData(response) {
            this.preloader = false;
            this.formData = {
                ...response.data,
                name: this.normalizeLocaleObject(response.data.name),
                remark: this.normalizeLocaleObject(response.data.remark),
            };
            this.iconSearch = '';
            this.showIconDropdown = false;
        },
        selectIcon(iconId) {
            this.formData.icon = iconId;
            this.showIconDropdown = false;
            this.iconSearch = '';
        },
        refreshFeatherIcons() {
            this.$nextTick(() => {
                feather.replace();
            });
        },
        toggleIconDropdown() {
            this.showIconDropdown = !this.showIconDropdown;
            this.$nextTick(() => this.refreshFeatherIcons());
        },
        handleClickOutside(event) {
            if (this.$refs.iconPicker && !this.$refs.iconPicker.contains(event.target)) {
                this.showIconDropdown = false;
            }
        },
        formatIconLabel(id) {
            const label = id
                .split('-')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');

            return `<i data-feather="${id}">${label}</i>`;
        },
        normalizeLocaleObject(val) {
            if (!val) return {};
            return Array.isArray(val) && val.length === 0 ? {} : val;
        }
    },
    created() {
        this.iconOptions = FEATHER_ICON_IDS.map(id => ({
            id,
            name: this.formatIconLabel(id),
        }));
    },
    mounted() {
        this.refreshFeatherIcons();
        document.addEventListener('click', this.handleClickOutside);
    },
    updated() {
        this.refreshFeatherIcons();
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
}
</script>

<style scoped>
#select-icon {
    width: 15px;
    margin-right: 3px;
    color: #89897f;
}

.icon-picker {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.75rem;
    background-color: #f8f9fb;
}

.icon-picker__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(48px, 1fr));
    gap: 0.5rem;
    max-height: 260px;
    overflow-y: auto;
}

.icon-picker__item {
    border: 1px solid #e5e7eb;
    background-color: #fff;
    border-radius: 0.375rem;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all .15s ease-in-out;
    position: relative;
    padding: 0;
}

.icon-picker__item:hover {
    border-color: #5a8dee;
    box-shadow: 0 0 0 2px rgba(90, 141, 238, 0.15);
}

.icon-picker__item.selected {
    background-color: #e8f1ff;
    border-color: #5a8dee;
}

.icon-picker__item svg {
    width: 20px;
    height: 20px;
    color: #6c757d;
}

.icon-picker-wrapper {
    position: relative;
}

.icon-picker-select {
    cursor: pointer;
    user-select: none;
    min-height: 44px;
}

.icon-picker-select__icon {
    width: 28px;
    height: 28px;
    border: 1px solid #e5e7eb;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.75rem;
    background-color: #fff;
}

.icon-picker-select__icon svg {
    width: 18px;
    height: 18px;
    color: #6c757d;
}

.icon-picker-dropdown {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1050;
    margin-top: 6px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease-in-out;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.icon-picker__search .input-group-text {
    background-color: #fff;
}
</style>
